import * as React from 'react';
import classNames from 'classnames';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import Button, { ButtonProps } from '../../atoms/Button';
import Text from '../../atoms/Text';
import ActionsButton from '../ActionsButton';
import { useTabOverflow } from '../../../utils/useTabOverflow';

import styles from './PillsFilter.module.scss';

// Matches ActionsButton's own default `label` — kept as a reference (not an
// inline literal) so the hidden measurer below doesn't trip the
// no-unnecessary-curly-braces rule while still satisfying Button's required
// `children`.
const EMPTY_LABEL = '';

// Interfaces
// //////////////////////////

export interface SelectOptions {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface BaseProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  name: string;
  options: Array<SelectOptions>;
  label?: string;
  helperText?: string;
  errorText?: string;
  align?: 'left' | 'center' | 'right';
  size?: ButtonProps['size'];
  variant?: 'pill' | 'button';
  multiple?: boolean;
  multiline?: boolean;
}

export interface SingleSelectProps extends BaseProps {
  value: string;
  onChange?: (name: string, newValue: string) => void;
  multiple?: false;
}

export interface MultiSelectProps extends BaseProps {
  value: string[];
  onChange?: (name: string, newValue: string[]) => void;
  multiple: true;
}

export type Props = SingleSelectProps | MultiSelectProps;

/**
 * <PillsFilter> component
 */
export const PillsFilter: React.FunctionComponent<Props> = ({
  className,
  name,
  options,
  value,
  onChange,
  label,
  helperText,
  errorText,
  align = 'center',
  size = 'sm',
  variant = 'pill',
  multiple = false,
  multiline = false,
  ...htmlElementProps
}) => {
  const { containerRef, visibleCount } = useTabOverflow(options);

  const visibleOptions = React.useMemo(() => {
    // Ignore overflow if multiline mode
    if (multiline) return options;

    const selectedIdx = options.findIndex((opt) =>
      multiple
        ? Array.isArray(value) && value.includes(opt.id)
        : opt.id === value,
    );
    if (selectedIdx >= visibleCount) {
      return [options[selectedIdx], ...options.slice(0, visibleCount - 1)];
    }
    return options.slice(0, visibleCount);
  }, [multiline, multiple, options, value, visibleCount]);

  const hiddenOptions = React.useMemo(() => {
    // Ignore overflow if multiline mode
    if (multiline) return [];

    const selectedIdx = options.findIndex((opt) =>
      multiple
        ? Array.isArray(value) && value.includes(opt.id)
        : opt.id === value,
    );
    if (selectedIdx >= visibleCount) {
      return [
        ...options.slice(visibleCount - 1, selectedIdx),
        ...options.slice(selectedIdx + 1),
      ];
    }
    return options.slice(visibleCount);
  }, [multiline, multiple, options, value, visibleCount]);

  const handleClick = React.useCallback(
    (optId: string) => {
      if (onChange) {
        if (multiple) {
          const newValue = (() => {
            // The '' id is used for the all/every option
            // Clear selection when '' is selected
            if (optId === '') {
              return [''];
            }
            // Clear '' selection when any other option is selected
            if (value.length === 1 && value.at(0) === '') {
              return [optId];
            }
            if (Array.isArray(value)) {
              return value.includes(optId)
                ? value.filter((id) => id !== optId)
                : [...value, optId];
            }
            return [optId];
          })();
          (onChange as MultiSelectProps['onChange'])?.(name, newValue);
        } else {
          (onChange as SingleSelectProps['onChange'])?.(name, optId);
        }
      }
    },
    [multiple, name, onChange, value],
  );

  return (
    <div
      className={classNames(styles.wrapper, className)}
      ref={containerRef}
      {...htmlElementProps}
    >
      {label && (
        <Text className="mb-2" weight="bold">
          {label}
        </Text>
      )}

      {helperText && (
        <Text className="mb-2" size="sm1">
          {helperText}
        </Text>
      )}

      <div
        className={classNames(styles['options-container'], align, {
          'on-error': !!errorText,
          [variant]: variant,
          multiline,
        })}
      >
        {/* This are hidden in order to the useTabOverflow to be able to
        calculate the width */}
        {options.map((opt) => (
          <Button
            key={`measure-${opt.id}`}
            className={classNames(styles.pill)}
            style={{
              visibility: 'hidden',
              position: 'absolute',
              pointerEvents: 'none',
            }}
            data-tab /* this is essential for the useTabOverflow */
            size={size}
            variant="outline"
            disabled
          >
            {opt.label}
          </Button>
        ))}

        {/* Hidden "more" button measurer — matches the real ActionsButton
        below (no `size` prop there either, so it defaults to "md") so
        useTabOverflow can reserve room for it whenever some options don't
        fit; without this the last "visible" option ends up pushing the real
        button (and everything after it) past the container's edge. The
        empty child mirrors the empty `label` ActionsButton renders. */}
        <Button
          style={{
            visibility: 'hidden',
            position: 'absolute',
            pointerEvents: 'none',
          }}
          data-more-button
          size="md"
          variant="outline"
          icon={faEllipsisVertical}
          disabled
        >
          {EMPTY_LABEL}
        </Button>

        {visibleOptions.map((opt) => (
          <Button
            key={`visible-${opt.id}`}
            className={classNames(styles.pill, {
              active: multiple
                ? Array.isArray(value) && value.includes(opt.id)
                : opt.id === value,
            })}
            size={size}
            variant="outline"
            disabled={opt.disabled}
            onClick={() => handleClick(opt.id)}
          >
            {opt.label}
          </Button>
        ))}

        {hiddenOptions.length > 0 && (
          <ActionsButton label="">
            {(dismiss) =>
              hiddenOptions.map((opt) => (
                <Button
                  key={`hidden-${opt.id}`}
                  size={size}
                  variant="link"
                  disabled={opt.disabled}
                  aria-hidden
                  onClick={() => {
                    handleClick(opt.id);
                    dismiss();
                  }}
                >
                  {opt.label}
                </Button>
              ))
            }
          </ActionsButton>
        )}
      </div>

      {errorText && (
        <Text className="mt-2" size="sm1" color="red400">
          {errorText}
        </Text>
      )}
    </div>
  );
};

export default PillsFilter;
